import {
  InvestmentService,
  GetRoboChartByGoalOptions,
  GetRoboChartByPortParmetersOptions,
  RoboChartObjectTaxable,
  GetRoboChartByPortParmRiskOptions,
} from '@simpleinvest/ms-investment-sdk';

import {
  PerformanceReturnService,
  GetPerformanceReportOptions,
  GetPerformanceReportResponse,
  PostPerformanceSummaryOptions,
  PostPerformanceSummaryResponse,
  PostPerformanceCalculationOptions,
  PostTimeBasedCalculationsResponse,
} from '@simpleinvest/ms-performance-return-sdk';
import {apiKey, getLogger} from '@client/services/sdk';
// eslint-disable-next-line import/no-cycle
import {
  requestInterceptorFulfilledHandler,
  requestInterceptorRejectedHandler,
  responseInterceptorFulfilledHandler,
  responseInterceptorRejectedHandler,
} from '@client/interceptors';
import {SourcePlatform} from '@simpleinvest/ms-core-sdk';

export interface IraConstraints {
  contributionConstraints: ContributionConstraints;
  distributionConstraints: DistributionConstraints;
  /**
   * Indicates whether the specified account is a retirement account and subject to constraints.
   */
  retirementAccount: boolean;
}

export interface ValidDistributionReason {
  /**
   * Distribution reason name
   */
  reason: string;
  /**
   * Indicates if federal tax withholding is required for this distribution reason
   */
  withholdingEnabled: boolean;
  /**
   * Indicates if state tax withholding is required for this distribution reason
   */
  stateWithholdingEnabled: boolean;
}

export interface DistributionConstraints {
  closingFee?: ClosingFee;
  /**
   * Indicates whether a distribution of the total account balance is allowed
   */
  totalDistributionAllowed?: boolean;
  validDistributionReasons?: ValidDistributionReason[];
}

export interface ValidContribution {
  /**
   * Contribution reason name
   */
  reason: string;
  /**
   * Indicates whether this contribution type may be used for prior year contributions
   */
  previousYearContributionAllowed: boolean;
}

export interface ContributionConstraints {
  /**
   * Indicates whether contributions are allowed only for the current year.
   */
  onlyAllowCurrentYearContribution?: boolean;
  /**
   * Valid contributions reasons
   */
  validContributions?: ValidContribution[];
}

export interface ClosingFee {
  /**
   * Closing fee correspondent net amount
   */
  correspondentNetAmount: number;
  /**
   * Closing fee customer debit amount
   */
  customerDebitAmount: number;
  /**
   * Closing fee firm credit amout
   */
  firmCreditAmount: number;
  /**
   * Closing fee type
   */
  type: string;
}

export default {
  getInvestmentServiceInstance(): InvestmentService {
    return new InvestmentService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger: getLogger(),
    });
  },
  getPerformanceReturnServiceInstance(): PerformanceReturnService {
    return new PerformanceReturnService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger: getLogger(),
    });
  },
  async getDepositIraConstraints(accountNumber: string): Promise<IraConstraints> {
    const service: InvestmentService = await this.getInvestmentServiceInstance();

    return service.getIraConstraintsINCOMING({accountNumber});
  },

  async getWithdrawalIraConstraints(accountNumber: string): Promise<IraConstraints> {
    const service: InvestmentService = await this.getInvestmentServiceInstance();

    return service.getIraConstraintsOUTGOING({accountNumber});
  },

  async getAllocationByGoalId(payload: GetRoboChartByGoalOptions): Promise<RoboChartObjectTaxable[]> {
    const service: InvestmentService = await this.getInvestmentServiceInstance();
    const response = await service.getRoboChartByGoalParameters(payload);

    return response;
  },

  async getAllocationByPortId(payload: GetRoboChartByPortParmetersOptions): Promise<RoboChartObjectTaxable[]> {
    const service: InvestmentService = await this.getInvestmentServiceInstance();
    const response = await service.getRoboChartByPortParmeters(payload);

    return response;
  },

  async getPerformanceReportByAccountList(options: GetPerformanceReportOptions): Promise<GetPerformanceReportResponse> {
    const service: PerformanceReturnService = await this.getPerformanceReturnServiceInstance();
    const response = await service.getPerformanceTrendsByClientId(options);

    return response;
  },

  async postPerformanceSummary(payload: PostPerformanceSummaryOptions): Promise<PostPerformanceSummaryResponse> {
    const service: PerformanceReturnService = await this.getPerformanceReturnServiceInstance();

    return service.postPerformanceSummary(payload);
  },

  async postTimebasedCalendarCalculations(
    payload: PostPerformanceCalculationOptions
  ): Promise<PostTimeBasedCalculationsResponse> {
    const service: PerformanceReturnService = await this.getPerformanceReturnServiceInstance();

    return service.postTimebasedCalendarYearPerformance(payload);
  },
  async postTimebasedTrailingYearPerformance(
    payload: PostPerformanceCalculationOptions
  ): Promise<PostTimeBasedCalculationsResponse> {
    const service: PerformanceReturnService = await this.getPerformanceReturnServiceInstance();

    return service.postTimebasedTrailingYearPerformance(payload);
  },
  async getRiskAllocationByPortId(payload: GetRoboChartByPortParmRiskOptions): Promise<RoboChartObjectTaxable[]> {
    const service: InvestmentService = await this.getInvestmentServiceInstance();
    const response = await service.getRiskBasedAssetAllocation(payload);

    return response;
  },
};
